import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { GroupsService } from '@pages/statistics-page/services/groups.service';
import { StatisticsMenuElementService } from '@pages/statistics-page/services/statistics-menu-element.service';
import { StatisticsMenuValuesService } from '@pages/statistics-page/services/statistics-menu-values.service';
import DateUtils from '@shared/utils/date.utils';
import { distinctUntilChanged, take } from 'rxjs';

import { ChartResolution, GroupType } from '@pages/statistics-page/constants';
import { StatisticsMenuForm } from '@pages/statistics-page/interfaces';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
@Component({
  selector: 'app-statistics-menu',
  templateUrl: './statistics-menu.component.html',
  styleUrls: ['./statistics-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StatisticsMenuComponent implements OnInit, OnDestroy {
  protected statisticsMenuService = inject(StatisticsMenuValuesService);
  private router = inject(Router);
  private groupsService = inject(GroupsService);
  private elementService = inject(StatisticsMenuElementService);
  private statisticsMenuValuesService = inject(StatisticsMenuValuesService);

  elements: DvDynamicFormElement[];
  formGroup: FormGroup<FormOf<StatisticsMenuForm>>;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationEnd && this.isHeatmapChart()) {
        this.toggleStatisticsMenuFields();
      }
    });
  }

  ngOnInit(): void {
    this.elements = this.getFormElements();
    this.formGroup = DvFormUtils.createFormGroup(this.elements);

    this.setInitialFormValues();
    this.storeFormValues();

    this.fetchAndSetInitialGroups();
  }

  ngOnDestroy(): void {
    // fetch scores on component init just in case user added some new
    // departments in settings
    this.groupsService.hasLoaded = false;
  }

  onRemoveSelectionClick($event: number): void {
    this.statisticsMenuService.unpinGroup($event);
  }

  private get isFormGroupValid(): boolean {
    return (
      !this.formGroup.controls.dateFrom?.errors &&
      !this.formGroup.controls.dateTo?.errors &&
      !this.formGroup.controls.dateFrom?.errors
    );
  }

  private storeFormValues(): void {
    this.formGroup.valueChanges.pipe(distinctUntilChanged()).subscribe(val => {
      this.statisticsMenuService.activeMenuFilters$.next(
        this.isFormGroupValid ? (val as StatisticsMenuForm) : undefined
      );
    });
  }

  private setInitialFormValues(): void {
    const { dateFrom, dateTo, resolution } = this.formGroup.controls;

    if (dateFrom?.value && dateTo?.value) return;

    const activeMenuFilters = this.statisticsMenuService.activeMenuFiltersValue;

    if (activeMenuFilters) {
      this.formGroup.patchValue(activeMenuFilters);
      return;
    }

    const today: Date = new Date();
    const priorDate: Date = new Date(new Date().setDate(today.getDate() - 30));

    dateFrom?.setValue(DateUtils.convertDate(priorDate));
    dateTo?.setValue(DateUtils.convertDate(today));
    resolution.setValue(ChartResolution.Week);
    this.statisticsMenuService.activeMenuFilters$.next(
      this.formGroup.value as StatisticsMenuForm
    );
  }

  private getFormElements(): DvDynamicFormElement[] {
    if (this.isHeatmapChart()) {
      // TODO: amend default elements with heatmap menu values
      return this.elementService.getElements();
    } else {
      return this.elementService.getElements();
    }
  }

  private isHeatmapChart(): boolean {
    return this.router.url === '/statistics/heatmap';
  }

  private toggleStatisticsMenuFields(): void {
    // TODO: handle logic for heatmap menu fields
  }

  private fetchAndSetInitialGroups(): void {
    this.groupsService
      .getGroups()
      .pipe(take(1))
      .subscribe(groups => {
        if (this.statisticsMenuService.pinnedGroupsValue.length) return;

        const initialGroups = groups.filter(
          group => group.type !== GroupType.Department
        );

        if (initialGroups.length) {
          this.statisticsMenuValuesService.pinnedGroups$.next(initialGroups);
        }
      });
  }
}
